import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';
import { isArray } from 'lodash';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import * as action from './store/DamHistory.action';
import SearchBar from '../../../Components/Utility/FilterAction/SearchBar';
import Loader from '../../../Components/Utility/Loader/Loader';
import { Page_Size } from '../../../Components/helper';
import RenderLhsRhsValues from './RenderLhsRhsValues';
import ImageLazyLoader from '../../../Components/ImageLazyLoader';
import noImage from '../../../assets/images/no-image.png';

function DamHistory() {
  const dispatch = useDispatch();
  const { t } = useTranslation('common');
  const reducers = useSelector((redState) => redState);
  const {
    damHistory: { damHistoryList, damHistoryCount },
  } = reducers;
  const [state, setState] = useState({
    isLoading: false,
    history: [],
    users: [],
    searchValue: '',
    searchText: '',
    hasMore: true,
    pageIndex: 1,
    pageSize: Page_Size,
    searchLoaded: false,
  });
  useEffect(() => {
    if (damHistoryList?.length === damHistoryCount) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        hasMore: false,
      }));
    } else if (damHistoryList?.length !== damHistoryCount) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        hasMore: true,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [damHistoryList?.length]);

  useEffect(() => {
    return () => {
      dispatch(action.clearReducer());
    };
  }, [dispatch]);

  useEffect(() => {
    setState((prev) => ({ ...prev, isLoading: true }));
    const callFn = async () => {
      const newHistroyList = await Promise.all([dispatch(action.fetchDamHistoryList({ pageIndex: state.pageIndex, pageSize: state?.pageSize }))]);
      if (newHistroyList) {
        setState((prev) => ({
          ...prev,
          isLoading: false,
        }));
      }
    };
    callFn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (state.searchLoaded) {
        setState((debouncestate) => ({
          ...debouncestate,
          isLoading: true,
          pageIndex: 1,
          hasMore: true,
        }));
        const optns = {
          pageIndex: 1,
          pageSize: state.pageSize,
          searchText: state.searchText,
        };
        const res = await dispatch(action.fetchDamHistoryList(optns));
        if (res) {
          setState((debstate) => ({
            ...debstate,
            isLoading: false,
            searchLoaded: false,
          }));
        }
      }
    }, 1000);

    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.searchText, dispatch, state.pageIndex]);

  const handleSearch = (e) => {
    const targetValue = e.target.value;
    setState((searchState) => ({
      ...searchState,
      searchText: targetValue,
      searchLoaded: true,
    }));
  };

  const handleNextPageInScroll = async () => {
    let { pageIndex } = state;
    pageIndex += 1;
    setState((nextPagestate) => ({
      ...nextPagestate,
      pageIndex,
      isLoading: true,
    }));
    const params = {
      pageIndex,
      pageSize: state?.pageSize,
      searchText: state.searchText,
      isScrolled: true,
    };
    const result = await dispatch(action.fetchDamHistoryList(params));
    if (result) {
      setState((nextPagestate) => ({
        ...nextPagestate,
        isLoading: false,
      }));
    }
  };

  const memoizedHistory = useMemo(() => {
    return damHistoryList;
  }, [damHistoryList?.length]);

  const renderModuleWise = (historyObj, colType) => {
    if (historyObj?.section === 'DAM') {
      return <RenderLhsRhsValues historyFieldsData={historyObj?.fields} colType={colType} />;
    }
    return '--';
  };
  return (
    <>
      <div className="page-title-filter sticky">
        <div className="title-container d-flex align-items-center">
          <h4 className="title">{t('DAM_HISTORY.LISTING_VIEW.TITLE')}</h4>
        </div>
        <div className="search-filter action-new-row">
          <SearchBar
            onSearch={handleSearch}
            value={state.searchText}
            showMobileSearch={state.showMobileSearch}
            placeholder={t('DAM_HISTORY.LISTING_VIEW.SEARCH_PLACEHOLDER')}
            handleMobileSearch={() => {}}
          />
          <Link className="btn btn-white btn-back-lib " to="/digital-assets-management">
            {t('DAM_MAIN_VIEW.ADD_EDIT_VIEW.BACK')}
          </Link>
        </div>
      </div>
      <div className="scroll-container">
        {state?.isLoading && memoizedHistory?.length === 0 ? (
          <div className="text-center">
            <Loader />
          </div>
        ) : (
          <>
            <span className="top" />
            <span className="left" />
            <span className="right" />
            <div className="all-scroll">
              <div
                id="product_history"
                style={{
                  height: 'calc(100vh - 170px)',
                  overflow: 'scroll',
                  padding: '15px',
                }}
              >
                <InfiniteScroll
                  dataLength={memoizedHistory?.length}
                  loader={state.isLoading && <Loader />}
                  hasMore={state.hasMore}
                  next={handleNextPageInScroll}
                  scrollableTarget="product_history"
                  className="fixed-col-container"
                >
                  <table className="table custom-table m-0 table-product-history layout-fixed" id="product_history">
                    <thead>
                      <tr>
                        <th className="w-200px">{t('DAM_HISTORY.LISTING_VIEW.ASSET_TITLE')}</th>
                        <th className="w-150px">{t('DAM_HISTORY.LISTING_VIEW.ASSET_TYPE')}</th>
                        <th className="w-150px">{t('DAM_HISTORY.LISTING_VIEW.ASSET_FILE')}</th>
                        <th className="w-150px">{t('DAM_HISTORY.LISTING_VIEW.DESCRIPTION')}</th>
                        <th className="w-150px">{t('DAM_HISTORY.LISTING_VIEW.ACTION')}</th>
                        <th className="w-200px">{t('DAM_HISTORY.LISTING_VIEW.FROM')}</th>
                        <th className="w-200px">{t('DAM_HISTORY.LISTING_VIEW.TO')}</th>
                        <th className="w-150px">{t('DAM_HISTORY.LISTING_VIEW.USER')}</th>
                        <th className="w-150px">{t('DAM_HISTORY.LISTING_VIEW.DATE_TIME')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {isArray(memoizedHistory) && memoizedHistory.length > 0
                        ? memoizedHistory?.map((neHist) => {
                            return (
                              <tr key={neHist._id}>
                                <td>{neHist?.damId?.assetTitle || neHist?.damName}</td>
                                <td>{neHist?.damId?.assetType}</td>
                                <td>
                                  <ImageLazyLoader
                                    height={200}
                                    src={
                                      neHist?.damId?.croppedImage ||
                                      neHist?.damId?.imageUrl ||
                                      neHist?.damId?.imageLink ||
                                      neHist?.damId?.videoThumbnail ||
                                      noImage
                                    }
                                  />
                                </td>
                                <td>{neHist?.description || '--'}</td>
                                <td>{neHist?.action}</td>
                                <td className="p-0">{['Added', 'Archived', 'Unarchived'].includes(neHist?.action) ? '--' : renderModuleWise(neHist, 'lhs')}</td>
                                <td className="p-0">{['Archived', 'Unarchived'].includes(neHist?.action) ? '--' : renderModuleWise(neHist, 'rhs')}</td>
                                <td>{neHist?.userId?.name}</td>
                                <td>
                                  <span>{moment(neHist.createdAt).format('DD-MMMM-YYYY HH:mm')}</span>
                                </td>
                              </tr>
                            );
                          })
                        : null}
                    </tbody>
                  </table>
                </InfiniteScroll>
                {memoizedHistory?.length === 0 && <p className="mt-2 text-center">{t('DAM_HISTORY.LISTING_VIEW.NO_RECORDS')}</p>}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}

export default DamHistory;
